import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';

const StyledInput = styled.input<{ hasError?: boolean }>`
  padding: 10px 16px;
  background-color: #EBEBEB;
  border: 1px solid transparent;
  border-radius: 20px;
  color: #696969;
  font-family: 'Inter', sans-serif;
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  width: 100%;

  ${props => props.hasError && `
    border: 1.5px solid #F87171;
    box-shadow: 0 0 3px #F87171;
  `}
`;

interface CurrencyInputProps {
  id: string;
  name: string;
  value: number;
  onValueChange: (value: string | undefined, name?: string, values?: any) => void;
  placeholder?: string;
  intlConfig?: { locale: string; currency: string };
  hasError?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  id,
  name,
  value,
  onValueChange,
  placeholder = 'R$ 0,00',
  intlConfig = { locale: 'pt-BR', currency: 'BRL' },
  hasError,
  className,
  style,
}) => {
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return '';
    }
    const formatted = new Intl.NumberFormat(intlConfig.locale, {
      style: 'currency',
      currency: intlConfig.currency,
    }).format(value);
    return formatted;
  };

  const [displayValue, setDisplayValue] = useState<string>(formatCurrency(value));

  useEffect(() => {
    setDisplayValue(formatCurrency(value));
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numericValue = inputValue.replace(/[^0-9]/g, '');

    if (numericValue === '') {
      setDisplayValue('');
      onValueChange(undefined, name, { float: undefined, formatted: '' });
      return;
    }

    const floatValue = parseFloat(numericValue) / 100;
    const formattedValue = new Intl.NumberFormat(intlConfig.locale, {
      style: 'currency',
      currency: intlConfig.currency,
    }).format(floatValue);
    
    setDisplayValue(formattedValue);

    if (onValueChange) {
      onValueChange(formattedValue, name, { float: floatValue, formatted: formattedValue });
    }
  };

  const handleBlur = () => {
    const formatted = formatCurrency(value);
    setDisplayValue(formatted);
  };

  return (
    <StyledInput
      id={id}
      name={name}
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      hasError={hasError}
      style={style}
      type="text"
      inputMode="decimal"
    />
  );
};

export default CurrencyInput;
